import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterDataForm from './MasterDataForm';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { generateCustomIdsForList } from '../../utils/customIdGenerator';

const MasterDataList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [masterData, setMasterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [viewContactsRecord, setViewContactsRecord] = useState(null);
  const [viewDocumentsRecord, setViewDocumentsRecord] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [totalCount, setTotalCount] = useState(0);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/master-data');
      
      // Handle new response format
      let data;
      if (response.data.success) {
        data = response.data.data || [];
        setUserRole(response.data.userRole || 'user');
        setTotalCount(response.data.totalCount || 0);
      } else {
        // Fallback for old format
        data = response.data || [];
        setUserRole('user');
        setTotalCount(response.data?.length || 0);
      }

      const masterDataWithCustomIds = await generateCustomIdsForList('MasterData', data);
      setMasterData(masterDataWithCustomIds);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/master-data/${id}`);
      setIsConfirmVisible(false);
      setRecordToDelete(null);
      fetchMasterData();
    } catch (error) {
      console.error('Failed to delete master data:', error);
      alert('Failed to delete master data');
    }
  };

  return (
    <div className="space-y-3">
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm"
            onClick={() => {
              setSelectedRecord(null);
              setIsModalVisible(true);
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Master Data
          </button>
          
          {/* Role indicator */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Role:</span>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              userRole === 'admin' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {userRole === 'admin' ? 'Admin' : 'User'}
            </span>
          </div>
        </div>
        
        {/* Data count */}
        <div className="text-sm text-gray-600">
          {userRole === 'admin' ? 'All Master Data' : 'Your Master Data'}: {totalCount} records
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 text-sm font-medium">Loading data...</span>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custom ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Turnover</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {masterData.map((record) => (
                <tr key={record._id}>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.customId || record._id}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    <button
                      onClick={() => navigate(`/client/${record._id}`)}
                      className="text-blue-600 hover:text-blue-900 font-medium hover:underline"
                    >
                      {record.clientName}
                    </button>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">{record.clientType?.name || 'N/A'}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    <div>
                      {record.turnover ? (
                        record.turnover < 20000000 ? 'Less than 2 Crore' : 'More than 2 Crore'
                      ) : (
                        <span className="text-gray-400">Not specified</span>
                      )}
                      
                      {/* UDYOG AADHAAR Status */}
                      {(() => {
                        const entityTypeName = record.clientType?.name || '';
                        const isLessThan2Crore = record.turnover > 0 && record.turnover < 20000000;
                        
                        // Define the entity types that require UDYOG AADHAAR
                        const udyogAadhaarEntityTypes = [
                          'Government Department',
                          'Statutory Body', 
                          'Society',
                          'Partnership Firm',
                          'Body Incorporate',
                          'Limited Liability Partnership'
                        ];
                        
                        // Check if the entity type requires UDYOG AADHAAR (case-insensitive)
                        const requiresUdyogAadhaar = isLessThan2Crore && udyogAadhaarEntityTypes.some(type => 
                          entityTypeName.toLowerCase() === type.toLowerCase()
                        );
                        
                        // Check if UDYOG AADHAAR document exists in master data (case-insensitive)
                        const hasUdyogAadhaar = Object.keys(record.documents || {}).some(key => 
                          key.toLowerCase().includes('udyog') || key.toLowerCase().includes('aadhaar')
                        );
                        
                        if (requiresUdyogAadhaar) {
                          return (
                            <div className="mt-1">
                              {hasUdyogAadhaar ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  UDYOG AADHAAR
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  UDYOG AADHAAR Required
                                </span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    {record.clientAddress}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    {record.createdBy ? (
                      <div className="text-xs">
                        <div className="font-medium">
                          {record.createdBy.firstName} {record.createdBy.lastName}
                        </div>
                        <div className="text-gray-500">{record.createdBy.email}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">Unknown</span>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      record.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {record.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => {
                        setSelectedRecord(record);
                        setIsModalVisible(true);
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      className="text-green-600 hover:text-green-900"
                      onClick={() => setViewContactsRecord(record)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </button>
                    <button
                      className="text-yellow-600 hover:text-yellow-900"
                      onClick={() => setViewDocumentsRecord(record)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                    <button
                      className="text-red-600 hover:text-red-900"
                      onClick={() => {
                        setRecordToDelete(record);
                        setIsConfirmVisible(true);
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MasterDataForm
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedRecord(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setSelectedRecord(null);
          fetchMasterData();
        }}
        record={selectedRecord}
      />

      {/* Confirmation Modal */}
      {isConfirmVisible && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Master Data</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Are you sure you want to delete this master data? This action cannot be undone.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => handleDelete(recordToDelete._id)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setIsConfirmVisible(false);
                    setRecordToDelete(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Contacts Modal */}
      {viewContactsRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Contact Persons</h3>
                <div className="space-y-4">
                  {viewContactsRecord.contactPersons.map((contact, index) => (
                    <div key={index} className="border-b pb-3">
                      <p className="font-medium">{contact.title} {contact.name}</p>
                      <p className="text-gray-600">{contact.email}</p>
                      <p className="text-gray-600">{contact.phoneNumbers.join(', ')}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setViewContactsRecord(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Documents Modal */}
      {viewDocumentsRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Documents</h3>
                <div className="space-y-4">
                  {Object.entries(viewDocumentsRecord.documents || {}).map(([key, doc]) => (
                    doc && (
                      <div key={key} className="border-b pb-3">
                        <p className="font-medium">{doc.label || key.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-600">{doc.fileName}</span>
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setViewDocumentsRecord(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterDataList;